import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription } from './ui/sheet';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

export const CartDrawer = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    updateQuantity, 
    removeFromCart, 
    clearCart, 
    getTotal
  } = useCart();
  
  const total = getTotal();

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Seu carrinho está vazio!');
      return;
    }
    onOpenChange(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-xl font-black">
            <ShoppingBag className="w-6 h-6 text-primary" />
            Seu Carrinho
          </SheetTitle>
          <SheetDescription>
            Adicione produtos e finalize pelo WhatsApp
          </SheetDescription>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <ShoppingBag className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground">
              Seu carrinho está vazio
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Adicione produtos para continuar
            </p>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {cartItems.map(item => (
                <div
                  key={item.id}
                  className="flex gap-3 bg-muted/50 rounded-xl p-3"
                  data-testid={`cart-item-${item.id}`}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm line-clamp-2">{item.name}</h4>
                    <p className="text-primary font-bold mt-1">
                      R$ {(item.price * item.quantity).toFixed(2).replace('.', ',')}
                    </p>
                    
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        data-testid={`cart-decrement-${item.id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="font-bold w-8 text-center">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        data-testid={`cart-increment-${item.id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                        onClick={() => {
                          removeFromCart(item.id);
                          toast.info(`${item.name} removido do carrinho`);
                        }}
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Separator />

            {/* Payment Info */}
            <div className="bg-muted/50 rounded-xl p-4 my-4">
              <p className="text-sm font-medium mb-2">Formas de Pagamento</p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="bg-[#25D366]/20 text-[#25D366] px-2 py-1 rounded-full font-medium">PIX</span>
                <span className="bg-primary/10 text-primary px-2 py-1 rounded-full">Cartão</span>
                <span className="bg-primary/10 text-primary px-2 py-1 rounded-full">Dinheiro</span>
              </div>
            </div>

            <Separator />

            {/* Footer */}
            <SheetFooter className="flex-col gap-4 pt-4">
              <div className="flex items-center justify-between w-full">
                <span className="text-lg font-medium">Total</span>
                <span className="text-2xl font-black text-primary">
                  R$ {total.toFixed(2).replace('.', ',')}
                </span>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-6 rounded-full text-lg"
                data-testid="checkout-button"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Finalizar Pedido
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  clearCart();
                  toast.info('Carrinho limpo');
                }}
                className="w-full rounded-full"
                data-testid="clear-cart-button"
              >
                Limpar carrinho
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
